import {
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
} from "@mui/material";
import { useId } from "react";

export interface MultiSelectFilterProps<T extends string> {
  label: string;
  options: readonly T[];
  optionLabel: (option: T) => string;
  value: T[];
  onChange: (value: T[]) => void;
  isValidOption: (v: string) => v is T;
  /** Shown under the control when `value` is empty. */
  emptyHelperText: string;
  width?: number;
}

/**
 * A "Status"/"Sections"-style multi-select: a "Select all" sentinel with an
 * indeterminate checkbox, a tri-state summary ("None selected" / "All
 * selected" / comma-joined labels), and helper text when nothing is
 * selected. Shared by the Discrepancy Review dialog's Status and Sections
 * filters so the two don't drift out of sync with each other.
 */
export function MultiSelectFilter<T extends string>({
  label,
  options,
  optionLabel,
  value,
  onChange,
  isValidOption,
  emptyHelperText,
  width = 220,
}: MultiSelectFilterProps<T>) {
  const labelId = useId();
  const allSelected = options.every((o) => value.includes(o));

  return (
    <FormControl size="small" sx={{ width, position: "relative" }}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select<string[]>
        multiple
        labelId={labelId}
        value={value}
        label={label}
        onChange={(e) => {
          const raw = e.target.value;
          // MUI emits a comma-joined string in native-select mode; otherwise it's a string[].
          const rawArray: string[] = typeof raw === "string" ? raw.split(",") : raw;

          if (rawArray.includes("__all__")) {
            onChange(allSelected ? [] : [...options]);
            return;
          }

          onChange(rawArray.filter(isValidOption));
        }}
        displayEmpty
        renderValue={(selected) => {
          if (selected.length === 0) {
            return "None selected";
          }
          if (selected.length === options.length) {
            return "All selected";
          }
          return selected.filter(isValidOption).map(optionLabel).join(", ");
        }}
      >
        <MenuItem value="__all__">
          <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={!allSelected && value.length > 0}
          />
          <ListItemText primary="Select all" />
        </MenuItem>
        {options.map((o) => (
          <MenuItem key={o} value={o}>
            <Checkbox size="small" checked={value.includes(o)} />
            <ListItemText primary={optionLabel(o)} />
          </MenuItem>
        ))}
      </Select>
      {value.length === 0 && (
        <FormHelperText
          sx={{
            // Float out of flow so wrapping copy doesn't push the dialog content downward.
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            m: 0,
            mt: 0.5,
          }}
        >
          {emptyHelperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}
